import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth, useIdentityFingerprint } from '../auth/AuthContext'
import { api } from './client'
import type {
  Account,
  ConfigurationGraph,
  AuditFilters,
  AuditPage,
  AccountDriverMapping,
  AllocationRun,
  CostCenter,
  DriverSnapshotRow,
  AlertKindInfo,
  AlertRecipient,
  BedType,
  CensusEntry,
  CensusEntryType,
  CostBehavior,
  CostMatrix,
  CostTraceability,
  OrganizationSettings,
  OrganizationTheme,
  PatientDaysRow,
  Ppd,
  ThemeTokenCatalog,
  TimeZoneOption,
  ResultByCostCenter,
  ResultMatrixRow,
  ResultRollup,
  CostCenterCoverage,
  CostDriver,
  CoverageState,
  DriverAllocation,
  DriverHealthState,
  DriverStatus,
  DriverType,
  Period,
  QcFinding,
  QcRule,
  QcRun,
  Schedule,
} from './types'

/*
  Every key carries the identity fingerprint (`userId|orgId`).

  Without it, switching organizations would serve the previous organization's cached
  rows under the new organization's name — a screen that looks completely normal and is
  entirely wrong. The OrgSwitcher also clears the cache on switch; this is the belt to
  that pair of braces, and it is what keeps two organizations' data from ever occupying
  the same cache entry.
*/
export const queryKeys = {
  costCenters: (fp: string, shape: 'tree' | 'flat', includeInactive: boolean) =>
    ['cost-centers', fp, shape, includeInactive] as const,
  costCenterCoverage: (fp: string, periodId: number | null) =>
    ['cost-center-coverage', fp, periodId] as const,
  accounts: (fp: string, search: string, coverage: CoverageState | null, includeInactive: boolean) =>
    ['accounts', fp, search, coverage, includeInactive] as const,
  periods: (fp: string) => ['periods', fp] as const,
  costDrivers: (fp: string, type: string | null, status: string | null, health: string | null) =>
    ['cost-drivers', fp, type, status, health] as const,
  driverAllocations: (fp: string, driverId: number | null) =>
    ['driver-allocations', fp, driverId] as const,
  accountDrivers: (fp: string, accountId: number | null) =>
    ['account-drivers', fp, accountId] as const,
  allocableCostCenters: (fp: string) => ['allocable-cost-centers', fp] as const,
  schedules: (fp: string) => ['schedules', fp] as const,
  organizationSettings: (fp: string) => ['organization-settings', fp] as const,
  timeZones: () => ['time-zones'] as const,
  // Not fingerprinted: the catalogue is a property of the deployment, not of the
  // organization, and every organization is offered the same tokens.
  themeTokens: () => ['theme-tokens'] as const,
  alertRecipients: (fp: string) => ['alert-recipients', fp] as const,
  alertKinds: () => ['alert-kinds'] as const,
  bedTypes: (fp: string) => ['bed-types', fp] as const,
  censusLedger: (fp: string, costCenterId: number | null, bedTypeId: number | null) =>
    ['census-ledger', fp, costCenterId, bedTypeId] as const,
  patientDays: (fp: string, periodId: number | null) => ['patient-days', fp, periodId] as const,
  ppd: (fp: string, runId: number | null) => ['ppd', fp, runId] as const,
  costMatrix: (fp: string, runId: number | null) => ['cost-matrix', fp, runId] as const,
  audit: (fp: string, query: AuditQuery) =>
    ['audit', fp, query.tableName, query.action, query.userName, query.keyValue,
     query.skip, query.take] as const,
  auditFilters: (fp: string) => ['audit-filters', fp] as const,
  configurationGraph: (fp: string, periodId: number | null) =>
    ['configuration-graph', fp, periodId] as const,
}

// --------------------------------------------------------------------------- reads

export function useCostCenters(shape: 'tree' | 'flat', includeInactive = false) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.costCenters(fp, shape, includeInactive),
    queryFn: () =>
      api.get<CostCenter[]>(`/api/cost-centers?shape=${shape}&includeInactive=${includeInactive}`),
  })
}

export function useCostCenterCoverage(periodId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.costCenterCoverage(fp, periodId),
    queryFn: () => api.get<CostCenterCoverage[]>(`/api/cost-centers/coverage?periodId=${periodId}`),
    enabled: periodId !== null,
  })
}

export function useAccounts(search: string, coverage: CoverageState | null, includeInactive = false) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.accounts(fp, search, coverage, includeInactive),
    queryFn: () => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (coverage) params.set('coverage', coverage)
      if (includeInactive) params.set('includeInactive', 'true')
      return api.get<Account[]>(`/api/accounts?${params}`)
    },
  })
}

export function usePeriods() {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.periods(fp),
    queryFn: () => api.get<Period[]>('/api/periods'),
  })
}

// ----------------------------------------------------------------------- mutations

/**
 * Invalidates every cost-center-derived query.
 *
 * Deliberately broad: a single reparent changes materialized paths, depths and
 * HasChildren across two subtrees, and the coverage view reads the current hierarchy.
 * Trying to patch the cache surgically here would be a reliable source of a stale tree
 * that disagrees with the database — the exact class of bug this application exists to
 * make impossible in its own numbers.
 */
function useInvalidateCostCenters() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['cost-centers'] })
    void client.invalidateQueries({ queryKey: ['cost-center-coverage'] })
    // The overview draws cost centers, so a rename or a reparent changes it too.
    void client.invalidateQueries({ queryKey: ['configuration-graph'] })

    /*
        The QC run itself, so the dashboard notices it is now out of date.

        NOT the findings. Refetching those returns the same stored rows, because a finding
        records what a run reported and nothing recomputes it. What changes is the
        staleness the API attaches to the RUN at read time, and that is what puts the
        banner up. Invalidating findings here would be cost without effect, and would imply
        they are live when they are not.
    */
    void client.invalidateQueries({ queryKey: ['qc-latest'] })
  }
}

export function useCreateCostCenter() {
  const invalidate = useInvalidateCostCenters()
  return useMutation({
    mutationFn: (body: {
      code: string
      name: string
      parentCostCenterId: number | null
      isAllocable: boolean
    }) => api.post<CostCenter>('/api/cost-centers', body),
    onSuccess: invalidate,
  })
}

export function useUpdateCostCenter() {
  const invalidate = useInvalidateCostCenters()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number
      code: string
      name: string
      isAllocable: boolean
      isActive: boolean
    }) => api.put<CostCenter>(`/api/cost-centers/${id}`, body),
    onSuccess: invalidate,
  })
}

export function useMoveCostCenter() {
  const invalidate = useInvalidateCostCenters()
  return useMutation({
    mutationFn: ({ id, newParentId }: { id: number; newParentId: number | null }) =>
      api.post<CostCenter>(`/api/cost-centers/${id}/move`, { newParentId }),
    onSuccess: invalidate,
  })
}

export function useDeleteCostCenter() {
  const invalidate = useInvalidateCostCenters()
  return useMutation({
    mutationFn: (id: number) => api.del(`/api/cost-centers/${id}`),
    onSuccess: invalidate,
  })
}

// ------------------------------------------------------------- drivers and maps

export function useCostDrivers(
  type: DriverType | null = null,
  status: DriverStatus | null = null,
  health: DriverHealthState | null = null,
) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.costDrivers(fp, type, status, health),
    queryFn: () => {
      const params = new URLSearchParams()
      if (type) params.set('type', type)
      if (status) params.set('status', status)
      if (health) params.set('health', health)
      return api.get<CostDriver[]>(`/api/cost-drivers?${params}`)
    },
  })
}

export function useDriverAllocations(driverId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.driverAllocations(fp, driverId),
    queryFn: () => api.get<DriverAllocation[]>(`/api/cost-drivers/${driverId}/allocations`),
    enabled: driverId !== null,
  })
}

/** The set a driver may target: allocable and active, at any depth (D8). */
export function useAllocableCostCenters() {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.allocableCostCenters(fp),
    queryFn: () => api.get<CostCenter[]>('/api/cost-centers/allocable'),
  })
}

export function useAccountDrivers(accountId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.accountDrivers(fp, accountId),
    queryFn: () => api.get<AccountDriverMapping[]>(`/api/accounts/${accountId}/drivers`),
    enabled: accountId !== null,
  })
}

function useInvalidateDrivers() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['cost-drivers'] })
    void client.invalidateQueries({ queryKey: ['driver-allocations'] })
    // Every edge on the overview comes from a driver's allocations or an account's
    // map, so it is stale the moment either changes.
    void client.invalidateQueries({ queryKey: ['configuration-graph'] })
    // An account's coverage badge depends on its map, and a driver's health depends on
    // how many accounts route through it — so both move together.
    void client.invalidateQueries({ queryKey: ['account-drivers'] })
    void client.invalidateQueries({ queryKey: ['accounts'] })
    void client.invalidateQueries({ queryKey: ['cost-center-coverage'] })
    // See useInvalidateCostCenters: the run, so the dashboard notices it is out of date.
    // Not the findings, which are a record and do not change.
    void client.invalidateQueries({ queryKey: ['qc-latest'] })
  }
}

export function useCreateCostDriver() {
  const invalidate = useInvalidateDrivers()
  return useMutation({
    mutationFn: (body: {
      code: string
      name: string
      description: string | null
      driverType: DriverType
    }) => api.post<CostDriver>('/api/cost-drivers', body),
    onSuccess: invalidate,
  })
}

export function useUpdateCostDriver() {
  const invalidate = useInvalidateDrivers()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number
      code: string
      name: string
      description: string | null
      status: DriverStatus
    }) => api.put<CostDriver>(`/api/cost-drivers/${id}`, body),
    onSuccess: invalidate,
  })
}

export function useReplaceDriverAllocations() {
  const invalidate = useInvalidateDrivers()
  return useMutation({
    mutationFn: ({
      id,
      effectiveFrom,
      lines,
    }: {
      id: number
      effectiveFrom: string
      lines: { costCenterId: number; percent: number }[]
    }) =>
      api.put<DriverAllocation[]>(`/api/cost-drivers/${id}/allocations`, { effectiveFrom, lines }),
    onSuccess: invalidate,
  })
}

export function useReplaceAccountDrivers() {
  const invalidate = useInvalidateDrivers()
  return useMutation({
    mutationFn: ({
      accountId,
      effectiveFrom,
      lines,
    }: {
      accountId: number
      effectiveFrom: string
      lines: { costDriverId: number; percent: number }[]
    }) =>
      api.put<AccountDriverMapping[]>(`/api/accounts/${accountId}/drivers`, {
        effectiveFrom,
        lines,
      }),
    onSuccess: invalidate,
  })
}

export function useBulkApplyAccountDrivers() {
  const invalidate = useInvalidateDrivers()
  return useMutation({
    mutationFn: (body: {
      accountIds: number[]
      effectiveFrom: string
      lines: { costDriverId: number; percent: number }[]
    }) => api.post<{ accountsUpdated: number }>('/api/accounts/drivers/bulk', body),
    onSuccess: invalidate,
  })
}

// -------------------------------------------------------------- allocation runs

export function useAllocationRuns(periodId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: ['allocation-runs', fp, periodId] as const,
    queryFn: () =>
      api.get<AllocationRun[]>(
        periodId === null ? '/api/allocation-runs' : `/api/allocation-runs?periodId=${periodId}`,
      ),
  })
}

/**
 * Polls a run until it reaches a terminal state.
 *
 * §9: long-running operations return 202 with a status URL and the UI polls at 2s. No
 * SignalR in v1 — polling a status endpoint is enough for an operation measured in
 * seconds, and it has no connection state to get wrong.
 */
export function useAllocationRun(runId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: ['allocation-run', fp, runId] as const,
    queryFn: () => api.get<AllocationRun>(`/api/allocation-runs/${runId}`),
    enabled: runId !== null,
    refetchInterval: (query) => (query.state.data?.isTerminal === false ? 2000 : false),
  })
}

/*
  Posted and rolled-up results are different shapes, so they get different hooks rather
  than one returning a union. The rollup carries the Direct / Descendant / RolledUp
  split; the posted view carries a single amount. Collapsing them into one type would
  mean every consumer branching on which it got.
*/

export function useRunResultsByCostCenter(runId: number | null, enabled = true) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: ['run-results', fp, runId] as const,
    queryFn: () => api.get<ResultByCostCenter[]>(`/api/allocation-runs/${runId}/results`),
    enabled: runId !== null && enabled,
  })
}

export function useRunRollup(runId: number | null, enabled = true) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: ['run-rollup', fp, runId] as const,
    queryFn: () => api.get<ResultRollup[]>(`/api/allocation-runs/${runId}/results?rollup=true`),
    enabled: runId !== null && enabled,
  })
}

export function useRunMatrix(runId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: ['run-matrix', fp, runId] as const,
    queryFn: () => api.get<ResultMatrixRow[]>(`/api/allocation-runs/${runId}/matrix`),
    enabled: runId !== null,
  })
}

export function useRunSnapshot(runId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: ['run-snapshot', fp, runId] as const,
    queryFn: () => api.get<DriverSnapshotRow[]>(`/api/allocation-runs/${runId}/snapshot`),
    enabled: runId !== null,
  })
}

export function useStartRun() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { periodId: number; isPreview: boolean }) =>
      api.post<AllocationRun>('/api/allocation-runs', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['allocation-runs'] })
    },
  })
}

export function useCancelRun() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (runId: number) => api.post<AllocationRun>(`/api/allocation-runs/${runId}/cancel`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['allocation-runs'] })
      void client.invalidateQueries({ queryKey: ['allocation-run'] })
    },
  })
}

// ------------------------------------------------------------------- schedules

export interface SaveScheduleBody {
  name: string
  periodSelector: string
  cron: string
  enabled: boolean
  isPreview: boolean
}

/**
 * Admin only — the API returns 403 to everyone else, so the caller must gate on the
 * role rather than let a forbidden request fire on every render.
 */
export function useSchedules(enabled: boolean) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.schedules(fp),
    queryFn: () => api.get<Schedule[]>('/api/schedules'),
    enabled,
  })
}

export function useSaveSchedule() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ scheduleId, ...body }: SaveScheduleBody & { scheduleId?: number }) =>
      scheduleId === undefined
        ? api.post<Schedule>('/api/schedules', body)
        : api.put<Schedule>(`/api/schedules/${scheduleId}`, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['schedules'] }),
  })
}

export function useDeleteSchedule() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (scheduleId: number) => api.del(`/api/schedules/${scheduleId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['schedules'] }),
  })
}

// -------------------------------------------------- organization settings

/**
 * The organization's time zone, PPD mode, and white-label theme.
 *
 * Readable by everyone, unlike the rest of the settings surface: the schedule screen
 * shows times to every analyst, a screen that cannot say which zone "03:00" is in cannot
 * be read at all, and the theme has to be applied before the first screen paints.
 *
 * Gated on an organization being resolved. The API refuses rather than guessing when a
 * multi-organization user sends no header, so firing this from the theme provider —
 * which mounts above the organization chooser — would put a 400 on the console of every
 * user who belongs to two organizations, on every load.
 */
export function useOrganizationSettings() {
  const fp = useIdentityFingerprint()
  const { activeOrg } = useAuth()
  return useQuery({
    queryKey: queryKeys.organizationSettings(fp),
    queryFn: () => api.get<OrganizationSettings>('/api/organization/settings'),
    enabled: activeOrg !== null,
  })
}

/**
 * The zones the server will accept.
 *
 * Fetched rather than bundled, because the authority on which ids resolve is the machine
 * the API runs on — a list baked in here could offer one the API refuses, and the refusal
 * would arrive only after the admin saved. Long-lived: the set changes when the host's
 * zone database is updated, which is not within a session.
 */
export function useTimeZones(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.timeZones(),
    queryFn: () => api.get<TimeZoneOption[]>('/api/organization/settings/timezones'),
    staleTime: Infinity,
    enabled,
  })
}

export function useSetTimeZone() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (timeZoneId: string) =>
      api.put<OrganizationSettings>('/api/organization/settings/timezone', { timeZoneId }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['organization-settings'] })
      // Every schedule's next fire time and daylight-saving warning is computed in this
      // zone, so they are all stale the moment it changes.
      void client.invalidateQueries({ queryKey: ['schedules'] })
    },
  })
}

// --------------------------------------------------------- white-label theme

/**
 * The tokens this deployment will accept in a theme, and its host allow-lists.
 *
 * Fetched rather than bundled, for the same reason as the time zone list: the authority
 * on what the API accepts is the API. The allow-lists in particular are per-deployment
 * configuration that the SPA has no other way to learn, and an editor that cannot show
 * them leaves an admin to discover them through a 422 after they have pasted a logo.
 *
 * Long-lived — it changes when the API is redeployed or reconfigured, not within a
 * session.
 */
export function useThemeTokenCatalog(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.themeTokens(),
    queryFn: () => api.get<ThemeTokenCatalog>('/api/organization/settings/theme/tokens'),
    staleTime: Infinity,
    enabled,
  })
}

/**
 * Replaces the organization's theme, or clears it back to the product's own brand.
 *
 * `null` is the revert, not an omission — the whole document is written every time, so
 * that "put this colour back to the default" and "leave this colour alone" stay
 * different instructions.
 */
export function useSetTheme() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (theme: OrganizationTheme | null) =>
      api.put<OrganizationSettings>('/api/organization/settings/theme', { theme }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['organization-settings'] }),
  })
}

// ------------------------------------------------------------- cost matrix

export function useCostMatrix(runId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.costMatrix(fp, runId),
    queryFn: () => api.get<CostMatrix>(`/api/accounts/cost-matrix/${runId}`),
    enabled: runId !== null,
  })
}

export function useSetAccountClassification() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, costTraceability, costBehavior }: {
      accountId: number
      costTraceability: CostTraceability | null
      costBehavior: CostBehavior | null
    }) =>
      api.put<Account>(`/api/accounts/${accountId}/classification`, {
        costTraceability,
        costBehavior,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['accounts'] })
      // Every cell of every matrix is a regrouping of the accounts, so they are all stale.
      void client.invalidateQueries({ queryKey: ['cost-matrix'] })
    },
  })
}

// ----------------------------------------------------------------- PPD mode

/**
 * All of these are gated on PPD mode being on. The API refuses rather than returning
 * empty, so the caller passes `enabled` instead of firing a request that is always going
 * to be a 422.
 */
export function useBedTypes(enabled: boolean) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.bedTypes(fp),
    queryFn: () => api.get<BedType[]>('/api/census/bed-types?includeInactive=true'),
    enabled,
  })
}

export function useSaveBedType() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ bedTypeId, ...body }: {
      bedTypeId?: number
      code: string
      name: string
      relativeWeight: number
      sortOrder: number
    }) =>
      bedTypeId === undefined
        ? api.post<BedType>('/api/census/bed-types', body)
        : api.put<BedType>(`/api/census/bed-types/${bedTypeId}`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['bed-types'] })
      // A weight change restates the bed-type split of every PPD figure.
      void client.invalidateQueries({ queryKey: ['ppd'] })
    },
  })
}

export function useCensusLedger(
  costCenterId: number | null, bedTypeId: number | null, enabled: boolean,
) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.censusLedger(fp, costCenterId, bedTypeId),
    queryFn: () => {
      const query = new URLSearchParams()
      if (costCenterId !== null) query.set('costCenterId', String(costCenterId))
      if (bedTypeId !== null) query.set('bedTypeId', String(bedTypeId))
      return api.get<CensusEntry[]>(`/api/census/entries?${query}`)
    },
    enabled,
  })
}

export function useAddCensusEntry() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      costCenterId: number
      bedTypeId: number
      effectiveDate: string
      entryType: CensusEntryType
      beds: number
      note?: string | null
    }) => api.post<CensusEntry>('/api/census/entries', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['census-ledger'] })
      void client.invalidateQueries({ queryKey: ['patient-days'] })
      void client.invalidateQueries({ queryKey: ['ppd'] })
    },
  })
}

export function useDeleteCensusEntry() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (censusEntryId: number) => api.del(`/api/census/entries/${censusEntryId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['census-ledger'] })
      void client.invalidateQueries({ queryKey: ['patient-days'] })
      void client.invalidateQueries({ queryKey: ['ppd'] })
    },
  })
}

export function usePatientDays(periodId: number | null, enabled: boolean) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.patientDays(fp, periodId),
    queryFn: () => api.get<PatientDaysRow[]>(`/api/census/patient-days/${periodId}`),
    enabled: enabled && periodId !== null,
  })
}

export function usePpd(runId: number | null, enabled: boolean) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.ppd(fp, runId),
    queryFn: () => api.get<Ppd>(`/api/census/ppd/${runId}`),
    enabled: enabled && runId !== null,
  })
}

export function useSetPpdMode() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.put<OrganizationSettings>('/api/organization/settings/ppd-mode', { enabled }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['organization-settings'] }),
  })
}

// ----------------------------------------------------------- alert recipients

/** Admin only — the API returns 403 to everyone else. */
export function useAlertRecipients(enabled: boolean) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.alertRecipients(fp),
    queryFn: () =>
      api.get<AlertRecipient[]>('/api/organization/alert-recipients?includeInactive=true'),
    enabled,
  })
}

/** The set changes only when the code does, so it is fetched once and kept. */
export function useAlertKinds(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.alertKinds(),
    queryFn: () => api.get<AlertKindInfo[]>('/api/organization/alert-recipients/kinds'),
    staleTime: Infinity,
    enabled,
  })
}

export function useSaveAlertRecipient() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ alertRecipientId, ...body }: {
      alertRecipientId?: number
      email: string
      displayName: string | null
      alertKinds: string[] | null
      isActive: boolean
    }) =>
      alertRecipientId === undefined
        ? api.post<AlertRecipient>('/api/organization/alert-recipients', body)
        : api.put<AlertRecipient>(`/api/organization/alert-recipients/${alertRecipientId}`, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['alert-recipients'] }),
  })
}

export function useDeleteAlertRecipient() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (alertRecipientId: number) =>
      api.del(`/api/organization/alert-recipients/${alertRecipientId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['alert-recipients'] }),
  })
}

// ----------------------------------------------------------------- audit log

export interface AuditQuery {
  tableName: string
  action: string
  userName: string
  keyValue: string
  skip: number
  take: number
}

/** Admin only — the API returns 403 to everyone else. */
export function useAuditLog(enabled: boolean, query: AuditQuery) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.audit(fp, query),
    queryFn: () => {
      const params = new URLSearchParams()
      if (query.tableName) params.set('tableName', query.tableName)
      if (query.action) params.set('action', query.action)
      if (query.userName) params.set('userName', query.userName)
      if (query.keyValue) params.set('keyValue', query.keyValue)
      params.set('skip', String(query.skip))
      params.set('take', String(query.take))
      return api.get<AuditPage>(`/api/audit?${params}`)
    },
    enabled,
    // The log is append-only and grows constantly; a stale page is misleading in a way
    // a stale cost center tree is not.
    staleTime: 0,
  })
}

export function useAuditFilters(enabled: boolean) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.auditFilters(fp),
    queryFn: () => api.get<AuditFilters>('/api/audit/filters'),
    enabled,
  })
}

// -------------------------------------------------------------------------- QC

export function useLatestQcRun(periodId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: ['qc-latest', fp, periodId] as const,
    queryFn: async () => {
      // The endpoint answers 204 when no QC run exists yet — a normal state, not an
      // error, and what every period looks like before anyone presses the button. The
      // client returns undefined for 204; React Query will not accept undefined as
      // data, so it becomes an explicit null.
      const run = await api.get<QcRun | undefined>(`/api/periods/${periodId}/qc/latest`)
      return run ?? null
    },
    enabled: periodId !== null,
  })
}

export function useQcFindings(qcRunId: number | null, includeSuppressed: boolean) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: ['qc-findings', fp, qcRunId, includeSuppressed] as const,
    queryFn: () =>
      api.get<QcFinding[]>(
        `/api/qc/runs/${qcRunId}/findings?includeSuppressed=${includeSuppressed}`,
      ),
    enabled: qcRunId !== null,
  })
}

export function useQcRules() {
  return useQuery({
    queryKey: ['qc-rules'] as const,
    queryFn: () => api.get<QcRule[]>('/api/qc/rules'),
    // The rule catalogue changes only when the application is redeployed.
    staleTime: Infinity,
  })
}

export function useRunQc() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (periodId: number) => api.post<QcRun>('/api/qc/runs', { periodId }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['qc-latest'] })
      void client.invalidateQueries({ queryKey: ['qc-findings'] })
    },
  })
}

export function useSuppressFinding() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      ruleCode: string
      entityType: string
      entityId: number
      reason: string
      expiresUtc: string | null
    }) => api.post('/api/qc/suppressions', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['qc-latest'] })
      void client.invalidateQueries({ queryKey: ['qc-findings'] })
    },
  })
}

export function useCreateAccount() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      accountNumber: string
      name: string
      accountType: string
      isAllocable: boolean
    }) => api.post<Account>('/api/accounts', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['accounts'] })
      /*
          The overview too. A newly created allocable account routes nowhere yet, which
          is QC-A001's condition and the overview's headline problem -- and making an
          existing one allocable or inactive moves it in or out of that population. An
          overview that did not notice would show a clean graph over a period that is
          about to be refused.
      */
      void client.invalidateQueries({ queryKey: ['configuration-graph'] })
    },
  })
}

export function useUpdateAccount() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number
      accountNumber: string
      name: string
      accountType: string
      isAllocable: boolean
      isActive: boolean
    }) => api.put<Account>(`/api/accounts/${id}`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['accounts'] })
      /*
          The overview too. A newly created allocable account routes nowhere yet, which
          is QC-A001's condition and the overview's headline problem -- and making an
          existing one allocable or inactive moves it in or out of that population. An
          overview that did not notice would show a clean graph over a period that is
          about to be refused.
      */
      void client.invalidateQueries({ queryKey: ['configuration-graph'] })
    },
  })
}

// ------------------------------------------------------- the configuration graph

/**
 * Accounts, drivers and cost centers with the edges between them, in one request.
 *
 * `periodId` is optional and changes the question. Null resolves the effective-dated
 * edges against today, which is what the driver list already does. Naming a period
 * resolves them against that period's end date — the date the engine itself uses (§14) —
 * so the answer becomes "what would a run against this period do". Those differ exactly
 * when a change has been dated forward.
 */
export function useConfigurationGraph(periodId: number | null) {
  const fp = useIdentityFingerprint()
  return useQuery({
    queryKey: queryKeys.configurationGraph(fp, periodId),
    queryFn: () =>
      api.get<ConfigurationGraph>(
        `/api/configuration/graph${periodId === null ? '' : `?periodId=${periodId}`}`,
      ),
  })
}
